// Barra de abas genérica pra páginas-hub. Visual idêntico ao ClientTabs
// (border-bottom, aba ativa destacada). Controlada: recebe active + onChange
// (use com useTabParam pra estado via ?tab=).

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@control-center/ui';

export interface PageTab<T extends string> {
  id: T;
  label: string;
  icon?: LucideIcon;
}

export function PageTabs<T extends string>({
  tabs,
  active,
  onChange,
  ariaLabel,
}: {
  tabs: PageTab<T>[];
  active: T;
  onChange: (id: T) => void;
  ariaLabel: string;
}): JSX.Element {
  return (
    <nav
      role="tablist"
      aria-label={ariaLabel}
      className="flex flex-wrap items-center gap-1 border-b border-line"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`tab-${tab.id}-panel`}
            id={`tab-${tab.id}`}
            onClick={() => onChange(tab.id)}
            className={cn(
              '-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-[13px] font-medium transition-colors',
              isActive ? 'border-ink text-ink' : 'border-transparent text-muted hover:text-ink-2',
            )}
          >
            {tab.icon && <tab.icon size={14} strokeWidth={1.8} />}
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}

/**
 * Painel de uma aba — casa `aria-controls`/`aria-labelledby` com o botão de
 * mesma `id` no PageTabs, fechando a relação ARIA aba↔conteúdo pro leitor de tela.
 */
export function TabPanel({ id, children }: { id: string; children: ReactNode }): JSX.Element {
  return (
    <div role="tabpanel" id={`tab-${id}-panel`} aria-labelledby={`tab-${id}`}>
      {children}
    </div>
  );
}
